import useSimDetails from 'api/sims/useSimDetails';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import useSimStatuses from 'api/sims/useSimStatuses';
import useUserInfo from 'hooks/User/useUserInfo';
import { useCallback, useMemo } from 'react';
import useLegacyDashboard from 'state/contexts/LegacyDashboard/useLegacyDashboard';
import type { DropdownItemEntry, ManageSimItemHook } from '../types';

const useChangePlan: ManageSimItemHook = ({ context, selectedSims, disabled, segmentName }) => {
  // Single SIM case
  const simId = context === 'details' && selectedSims ? [...selectedSims.keys()][0] : undefined;
  const { simDetails } = useSimDetails(simId);
  const { deviceDetails } = useSimDeviceDetails(simId);
  const { claimedDeviceId } = deviceDetails ?? {};
  const legacyDashboardContext = useLegacyDashboard();

  const onClick = useCallback(() => {
    if (!legacyDashboardContext) {
      throw new Error('Legacy dashboard context is not available');
    }

    const { onBulkChangePlan } = legacyDashboardContext;

    if (claimedDeviceId) {
      onBulkChangePlan([claimedDeviceId], context === 'details' ? 'SIM Details' : 'SIM Inventory');
    } else {
      const deviceIds: Array<DeviceId | undefined> = [...(selectedSims?.values() ?? [])];
      onBulkChangePlan(
        deviceIds.filter<DeviceId>((id): id is DeviceId => !!id),
        context === 'details' ? 'SIM Details' : 'SIM Inventory'
      );
    }
  }, [claimedDeviceId, context, legacyDashboardContext, selectedSims]);

  const { networkStatusGroups } = useSimStatuses();

  const { organizationRole } = useUserInfo();

  const show =
    ['admin', 'manager'].includes(organizationRole) &&
    ((context === 'inventory' && ['all', 'active'].includes(segmentName ?? '')) ||
      (context === 'details' &&
        !!deviceDetails?.claimedDeviceId &&
        (networkStatusGroups?.deactivated?.findIndex(
          (state) => simDetails.networkStatus?.localeCompare(state) === 0
        ) ?? -1) === -1 &&
        (networkStatusGroups?.predeployment?.findIndex(
          (state) => simDetails.networkStatus?.localeCompare(state) === 0
        ) ?? -1) === -1));

  return useMemo<DropdownItemEntry | false>(
    () =>
      show && {
        children: 'Change data plan...',
        iconStart: 'CellTower',
        disabled,
        onClick,
        limit: 1000,
        id: `sim${context}-dataplan-changeplan`,
      },
    [context, disabled, onClick, show]
  );
};

export default useChangePlan;
